import React from 'react';
import { Check, X, Rabbit, Zap, Heart, Shield } from 'lucide-react';

const COMPARISONS = [
  {
    bad: 'Outils surchargés de fonctionnalités inutiles',
    good: 'Interface épurée, focalisée sur l\'essentiel'
  },
  {
    bad: 'Configuration complexe pendant des semaines',
    good: 'Opérationnel en 10 minutes chrono'
  },
  {
    bad: 'Courbe d\'apprentissage décourageante',
    good: 'Prise en main intuitive immédiate'
  },
  {
    bad: 'Tarification opaque et coûteuse',
    good: 'Prix transparent, adapté aux équipes'
  },
  {
    bad: 'Support distant et impersonnel',
    good: 'Équipe réactive, vraies réponses'
  }
];

const VALUES = [
  {
    icon: Zap,
    title: 'Rapidité',
    description: 'Chaque clic compte. Lapyno est optimisé pour la vitesse.'
  },
  {
    icon: Heart,
    title: 'Simplicité',
    description: 'Pas de menus à rallonge. Juste ce dont vous avez besoin.'
  },
  {
    icon: Shield,
    title: 'Fiabilité',
    description: 'Vos données sont en sécurité. Toujours disponible.'
  }
];

export function WhyLapynoSection() {
  return (
    <section id="why" className="py-24 md:py-32 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 text-sm font-medium mb-4">
            <Rabbit className="h-4 w-4" />
            Pourquoi Lapyno
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
            Conçu pour les équipes
            <br />
            <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
              qui veulent avancer
            </span>
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            Assez des outils complexes qui ralentissent plus qu'ils n'aident. 
            Lapyno est pensé pour le travail réel.
          </p>
        </div>

        {/* Comparison table */}
        <div className="max-w-4xl mx-auto mb-20">
          <div className="grid md:grid-cols-2 gap-6 md:gap-0">
            {/* Bad column */}
            <div className="p-6 md:p-8 bg-red-50 dark:bg-red-950/20 rounded-2xl md:rounded-r-none border border-red-200 dark:border-red-900/50">
              <div className="flex items-center gap-2 mb-6">
                <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                  <X className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <span className="font-semibold text-red-700 dark:text-red-400">Les autres outils</span>
              </div>
              <ul className="space-y-4">
                {COMPARISONS.map((item) => (
                  <li key={item.bad} className="flex items-start gap-3 text-red-700 dark:text-red-300">
                    <X className="h-5 w-5 mt-0.5 shrink-0 opacity-50" />
                    <span>{item.bad}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Good column */}
            <div className="p-6 md:p-8 bg-emerald-50 dark:bg-emerald-950/20 rounded-2xl md:rounded-l-none border border-emerald-200 dark:border-emerald-900/50 md:border-l-0">
              <div className="flex items-center gap-2 mb-6">
                <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                  <Check className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <span className="font-semibold text-emerald-700 dark:text-emerald-400">Lapyno</span>
              </div>
              <ul className="space-y-4">
                {COMPARISONS.map((item) => (
                  <li key={item.good} className="flex items-start gap-3 text-emerald-700 dark:text-emerald-300">
                    <Check className="h-5 w-5 mt-0.5 shrink-0" />
                    <span>{item.good}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Values */}
        <div className="grid md:grid-cols-3 gap-8">
          {VALUES.map((value) => {
            const Icon = value.icon;
            return (
              <div key={value.title} className="text-center">
                <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-indigo-100 to-violet-100 dark:from-indigo-900/30 dark:to-violet-900/30 mb-4">
                  <Icon className="h-8 w-8 text-indigo-600 dark:text-indigo-400" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                  {value.title}
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  {value.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
